<template>
  <div 
    class="min-h-screen text-gray-900 dark:text-gray-200 font-sans selection:bg-purple-500 selection:text-white"
    :class="{ 'dark': ['/login', '/register', '/logout'].includes(route.path) }"
  >
    <!-- Background Animation (Both Themes) -->
    <div class="fixed inset-0 z-0 pointer-events-none overflow-hidden bg-gray-50 dark:bg-[#0B0D17]">
       <!-- Static Stars (Dark Mode Only) -->
       <div class="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-0 dark:opacity-30 mix-blend-overlay"></div>
       
       <!-- Nebula Background -->
       <img src="~/assets/images/background.png" alt="Nebula Background" class="absolute inset-0 w-full h-full object-cover opacity-20 dark:opacity-40 animate-breathe" />
    </div>

    <!-- Main Content Area -->
    <div class="flex h-screen overflow-hidden">
      <!-- Sidebar (Hidden on Login) -->
      <aside 
        v-if="!isAuthPage"
        @mouseenter="onSidebarHover"
        @mouseleave="onSidebarLeave"
        class="flex-shrink-0 z-20 hidden md:flex glass-panel m-4 mr-0 flex-col transition-all duration-300 ease-in-out overflow-hidden"
        :class="sidebarExpanded ? 'w-64' : 'w-20'"
      >
        <div class="p-6 flex items-center space-x-3 min-h-[80px] relative">
          <div class="w-8 h-8 relative flex items-center justify-center">
            <img src="~assets/logos/pulsarLogo.svg"  alt="PulsarAI Logo" />
            <!-- Outer Glow (CSS) -->
            <div class="absolute inset-0 bg-purple-500/20 blur-xl rounded-full -z-10"></div>
          </div>
          <transition
            enter-active-class="transition-opacity duration-200"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-opacity duration-200"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <span v-show="sidebarExpanded" class="text-xl font-bold tracking-tight text-gray-900 dark:text-white whitespace-nowrap">PulsarAI</span>
          </transition>
          
          <!-- Pin Button (only visible when expanded) -->
          <transition
            enter-active-class="transition-opacity duration-200"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-opacity duration-200"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <button
              v-show="sidebarExpanded"
              @click="togglePin"
              class="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 transition-all duration-200"
              :class="sidebarPinned ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400 dark:text-gray-500'"
            >
            <Icon :name="sidebarPinned ? 'typcn:pin' : 'typcn:pin-outline'" size="24" />
            </button>
          </transition>
        </div>

        <nav class="flex-1 px-4 space-y-2 mt-4">
          <template v-for="item in menuItems" :key="item.name">
            <!-- Expandable Item -->
            <div v-if="item.children && item.children.length">
              <button 
                @click="toggleSubmenu(item)"
                class="w-full flex items-center py-3 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-gray-100 dark:hover:bg-white/10 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white group relative"
                :class="[
                  { 'bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white': isItemActive(item) },
                  sidebarExpanded ? 'px-4 justify-between' : 'pl-[14px]'
                ]"
              >
                <div class="flex items-center" :class="sidebarExpanded ? 'space-x-3' : ''">
                  <component :is="item.icon" class="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                  <transition
                    enter-active-class="transition-opacity duration-200"
                    enter-from-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="transition-opacity duration-200"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                  >
                    <span v-show="sidebarExpanded" class="whitespace-nowrap">{{ item.name }}</span>
                  </transition>
                </div>
                <svg 
                  v-show="sidebarExpanded"
                  class="w-4 h-4 transition-transform duration-200" 
                  :class="{ 'rotate-180': item.expanded }"
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
                <!-- Tooltip for collapsed state -->
                <span v-if="!sidebarExpanded" class="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                  {{ item.name }}
                </span>
              </button>
              
              <!-- Sub Items -->
              <transition
                enter-active-class="transition-all duration-200 ease-out"
                enter-from-class="opacity-0 max-h-0"
                enter-to-class="opacity-100 max-h-40"
                leave-active-class="transition-all duration-200 ease-in"
                leave-from-class="opacity-100 max-h-40"
                leave-to-class="opacity-0 max-h-0"
              >
                <div v-show="item.expanded && sidebarExpanded" class="ml-4 mt-1 space-y-1 overflow-hidden">
                  <NuxtLink 
                    v-for="child in item.children"
                    :key="child.name"
                    :to="child.path" 
                    class="flex items-center space-x-3 px-4 py-2 rounded-lg text-sm transition-all duration-200 hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 hover:text-gray-900 dark:text-gray-500 dark:hover:text-white"
                    active-class="bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white"
                  >
                    <component :is="child.icon" class="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                    <span class="whitespace-nowrap">{{ child.name }}</span>
                  </NuxtLink>
                </div>
              </transition>
            </div>

            <!-- Single Item -->
            <NuxtLink 
              v-else
              :to="item.path" 
              class="flex items-center py-3 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-gray-100 dark:hover:bg-white/10 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white group relative" 
              active-class="bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white shadow-sm dark:shadow-lg border border-gray-200 dark:border-white/5"
              :class="sidebarExpanded ? 'px-4 space-x-3' : 'pl-[14px]'"
            >
              <component :is="item.icon" class="w-5 h-5 flex-shrink-0" aria-hidden="true" />
              <transition
                enter-active-class="transition-opacity duration-200"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition-opacity duration-200"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <span v-show="sidebarExpanded" class="whitespace-nowrap">{{ item.name }}</span>
              </transition>
              <!-- Tooltip for collapsed state -->
              <span v-if="!sidebarExpanded" class="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                {{ item.name }}
              </span>
            </NuxtLink>
          </template>
        </nav>
        <!-- Signout Button -->
        <div class="p-4 mt-auto border-t border-white/5 dark:border-white/10 space-y-2">
          <button 
            @click="handleSignOut"
            class="w-full flex items-center py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors group relative" 
            :class="sidebarExpanded ? 'px-4 space-x-2' : 'pl-[14px]'"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
            <transition
              enter-active-class="transition-opacity duration-200"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100"
              leave-active-class="transition-opacity duration-200"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <span v-show="sidebarExpanded" class="whitespace-nowrap">Sign Out</span>
            </transition>
            <!-- Tooltip for collapsed state -->
            <span v-if="!sidebarExpanded" class="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              Sign Out
            </span>
          </button>
        </div>
      </aside>

      <!-- Main Content -->
      <main class="flex-1 overflow-auto z-10 relative flex flex-col">
        <!-- Top Bar (Hidden on Login) -->
        <header v-if="!isAuthPage" class="h-16 mx-6 mt-4 px-6 flex-shrink-0 flex items-center justify-between sticky top-4 z-30 rounded-2xl border border-gray-200 dark:border-white/10 bg-white/70 dark:bg-[#1E2130]/70 backdrop-blur-md shadow-lg transition-all duration-300">
          <!-- Search Bar -->
          <div class="flex-1 max-w-md">
            <div class="relative">
              <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input 
                type="text" 
                placeholder="Search..." 
                class="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          <!-- Right Section: Theme Toggle, Notifications & User Profile -->
          <div class="flex items-center space-x-3">
            <!-- Theme Toggle -->
            <ThemeToggle />
            
            <!-- Notification Bell -->
            <button class="relative p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-all duration-200">
              <svg class="w-6 h-6 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <!-- Notification Badge -->
              <span class="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>

            <!-- User Profile -->
            <div 
              v-if="authStore.isLoggedIn"
              class="flex items-center space-x-3 pl-3 border-l border-gray-200 dark:border-white/10"
            >
              <div class="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm shadow-lg">
                {{ userInitials }}
              </div>
              <div class="hidden sm:block">
                <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ userDisplayName }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">{{ userRole }}</p>
              </div>
            </div>
            <NuxtLink 
              v-else
              to="/login"
              class="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
            >
              Login
            </NuxtLink>
          </div>
        </header>

        <!-- Main Content -->
        <div class="flex-1">
          <slot />
        </div>
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Squares2X2Icon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  CubeIcon,
  Cog6ToothIcon,
  UsersIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  ShieldCheckIcon,
  SignalIcon,
  ArrowsRightLeftIcon,
  TableCellsIcon,
  CurrencyDollarIcon
} from '@heroicons/vue/24/outline'
import { useAuthStore } from '~/stores/authStore'
import { userLogout } from '@/scripts/auth/applicationAuth'

const authStore = useAuthStore()
const globalsStore = useGlobalsStore()
const { userProfile } = storeToRefs(globalsStore)

const userDisplayName = computed(() => userProfile.value?.fullName || authStore.user?.email || 'User')
const userInitials = computed(() => userProfile.value?.initials || authStore.user?.email?.substring(0, 2).toUpperCase() || 'U')
const userRole = computed(() => userProfile.value?.role || 'User')

const handleSignOut = async () => {
  await userLogout()
}

const route = useRoute()
const isAuthPage = computed(() => ['/login', '/signup', '/register', '/oauth', '/logout'].includes(route.path))

// Sidebar state
const sidebarExpanded = ref(false)
const sidebarPinned = ref(false)

const onSidebarHover = () => {
  sidebarExpanded.value = true
}

const onSidebarLeave = () => {
  if (!sidebarPinned.value) {
    sidebarExpanded.value = false
  }
}

const togglePin = () => {
  sidebarPinned.value = !sidebarPinned.value
}

// Menu Items Configuration
const menuItems = ref([
  {
    name: 'Overview',
    path: '/',
    icon: Squares2X2Icon
  },
  {
    name: 'Dashboards',
    icon: ChartBarIcon,
    expanded: false,
    children: [
      {
        name: 'User Management',
        path: '/dashboards/user-management',
        icon: UsersIcon
      },
      {
        name: 'Query History',
        path: '/dashboards/query-history',
        icon: ClockIcon
      }
    ]
  },
  {
    name: 'Ingest',
    path: '/ingest',
    icon: ArrowDownTrayIcon
  },
  {
    name: 'Governance',
    path: '/governance',
    icon: ShieldCheckIcon
  },
  {
    name: 'Observability',
    path: '/observability',
    icon: SignalIcon
  },
  {
    name: 'Data Flow',
    path: '/data-flow',
    icon: ArrowsRightLeftIcon
  },
  {
    name: 'Worksheets', // Added Worksheets navigation item
    path: '/worksheets',
    icon: TableCellsIcon
  }
])

const toggleSubmenu = (item: any) => {
  item.expanded = !item.expanded
}

// Logic to check if an item (parent or child) is active
const isItemActive = (item: any) => {
  if (item.path && route.path === item.path) {
    return true
  }
  if (item.children) {
    return item.children.some((child: any) => route.path === child.path || route.path.startsWith(child.path))
  }
  return false
}

// Auto-expand logic for initial load
watch(() => route.path, (newPath) => {
  menuItems.value.forEach(item => {
    if (item.children && item.children.some(child => newPath.startsWith(child.path))) {
      item.expanded = true
    }
  })
}, { immediate: true })
</script>

<style scoped>
/* Custom scrollbar for webkit */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
}
::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}
::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
